import { useCallback, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import {
  Button,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Tooltip,
  Box
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { Add, Delete, Edit, Tab as TabIcon } from '@mui/icons-material';
import Title from '../../components/Title';
import TabsDialog from './TabsDialog';
import {
  deleteTabAsync,
  getTabsAsync,
  selectTabs
} from '../../redux/slices/tabsSlice';

const PREFIX = 'TabsPage';

const classes = {
  header: `${PREFIX}-header`
};

const Root = styled('div')({
  [`& .${classes.header}`]: {
    marginBottom: 20
  }
});

const TabsPage = () => {
  const tabs = useSelector(selectTabs);
  const theme = useTheme();

  const dispatch = useDispatch();

  const [dialogState, setDialogState] = useState<{
    open: boolean;
    tabId?: string;
  }>({ open: false });

  const openDialog = useCallback(
    (tabId?: string) => () => setDialogState({ open: true, tabId }),
    []
  );

  const closeDialog = useCallback(() => setDialogState({ open: false }), []);

  const deleteTab = useCallback(
    (tabId: string) => async () => {
      await dispatch(deleteTabAsync(tabId));
      dispatch(getTabsAsync());
    },
    [dispatch]
  );

  return (
    <Root>
      <Grid
        container
        alignItems='center'
        className={classes.header}
        spacing={4}
      >
        <Grid item>
          <Title>Таби</Title>
        </Grid>
        <Grid item>
          <Button
            color='primary'
            startIcon={<Add />}
            variant='contained'
            onClick={openDialog()}
          >
            Додати
          </Button>
        </Grid>
      </Grid>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Іконка</TableCell>
              <TableCell>Назва</TableCell>
              <TableCell align='right'>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tabs.items.map(tab => (
              <TableRow key={tab._id}>
                <TableCell>
                  <Tooltip title="Таб" arrow>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        backgroundColor: 'rgba(33, 150, 243, 0.12)',
                        borderRadius: '8px'
                      }}
                    >
                      <TabIcon sx={{ fontSize: 24, color: '#2196F3' }} />
                    </Avatar>
                  </Tooltip>
                </TableCell>
                <TableCell>{tab.name}</TableCell>
                <TableCell align='right'>
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                    <Tooltip title='Редагувати'>
                      <IconButton
                        size='medium'
                        onClick={openDialog(tab._id)}
                        sx={{
                          bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                          color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                          borderRadius: '8px',
                          '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                        }}
                      >
                        <Edit fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title='Видалити'>
                      <IconButton
                        size='medium'
                        onClick={deleteTab(tab._id)}
                        sx={{
                          bgcolor: theme.palette.mode === 'light' ? '#F5F5F5' : '#424242',
                          color: theme.palette.mode === 'light' ? '#616161' : '#FFF',
                          borderRadius: '8px',
                          '&:hover': { bgcolor: theme.palette.mode === 'light' ? '#E0E0E0' : '#3A3A3A' }
                        }}
                      >
                        <Delete fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TabsDialog {...dialogState} onClose={closeDialog} />
    </Root>
  );
};

export default TabsPage;
